import { IMediated, IIndexable, IncludedType } from 'core-ts-types';
import { ORM } from './../engine/orm';
import { IStateMemento } from './../core/state_memento';
import * as _4 from 'lodash';

export type IUsersourceState<P> = { db: P };

export interface IUsersourceConstructor<P> {
  new(preferences: P): IUsersource<P>;
}

export interface IUsersource<P> extends IMediated, IStateMemento<IUsersourceState<P>> {
  set<K extends keyof P>(property: K, value: P[K]): this;
  get<K extends keyof P>(property: K): P[K] | undefined;
  update(preferences: Partial<P>): this;
  clear(): this;
  read(): P;
}

export class Usersource<P> implements IUsersource<P> {

  private __initialPreferences: P;

  constructor(protected _preferences: P) {
    this.__initialPreferences = _4.clone(this._preferences);

    _4.each(_4.keys(this._preferences), (property: keyof P) => {
      Object.defineProperty(this, property, {
        get: function(this: Usersource<P>) {
          return this.get(property);
        },
        set: function<K extends keyof P>(this: Usersource<P>, value: P[K]) {
          return this.set(property, value);
        }
      });
    });
  }

  /**
   * @param property an existing property
   */
  get<K extends keyof P>(property: K): P[K] | undefined {
    return this._preferences[property];
  }

  /**
   * @note this will force the ORM to run; for performance consider `#update` unless you need to map a property to a bindable component attribute
   * @param property an existing property
   * @param value a new value of the same type
   */
  set<K extends keyof P>(property: K, value: P[K]): this {
    this._preferences[property] = value;
    return this.__updateORM();
  }

  clear(): this {
    this._preferences = this.__initialPreferences;
    return this.__updateORM();
  }

  update(preferences: Partial<P>): this {
    _4.merge(this._preferences, preferences);
    return this.__updateORM();
  }

  import(state: IUsersourceState<P>): this {
    this._preferences = state.db;
    return this.__updateORM();
  }

  export(): IUsersourceState<P> {
    return {
      db: this._preferences
    };
  }

  /**
   * get source
   */
  read(): P {
    return this._preferences;
  }

  private __updateORM(): this {
    ORM
      .run(this as IMediated);
    return this;
  }

  /**
   * create a usersource with defined properties
   * @param preferences properties belonging to the usersource
   */
  static build<P>(preferences: P): IncludedType<IUsersource<P>, P> {
    return (new Usersource<P>(preferences) as IUsersource<P>) as IncludedType<IUsersource<P>, P>;
  }

}



// WEBPACK FOOTER //
// ./app/vendor/utility-ts-orm/dist/types/usersource.ts